import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as style from "./FeatureGroup.module.scss";
import { Search, LoadingSpinner } from "cprime-foundational-components";
import { triggerToast } from "../../utils/toast";
import { Box } from "@mui/material";
import { getGroupsWithFeatures } from "../../services/RequestAccess/RequestAccess";
import FeatureGroupSection from "./FeatureGroupSection";
import FeatureGroupSidebar from "./FeatureGroupSidebar";

import Button from "../../Components/Common/CprimeButton";
interface Feature {
  id: number;
  featureName: string;
  featureDescription: string | null;
  isChecked?: boolean;
}

interface ValueStream {
  groupId: number;
  id: number;
  name: string;
  description: string;
  groupDescription: string;
  features: Feature[];
  group: any;
}

const FeatureGroups = () => {
  const scssClass = style as any;

  const [data, setData] = useState<any>({ list: [] });
  const [searchValue, setSearchValue] = useState<string>("");
  const [isGroupSidePanelVisible, setIsGroupSidePanelVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [triggerEffect, setTriggerEffect] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchFeatureGroupsData = async () => {
    setIsLoading(true);
    try {
      const response = await getGroupsWithFeatures();
      const visibleGroups = response?.data?.visible ?? [];
      const hiddenGroups = response?.data?.hidden ?? [];

      const transformGroups = (groups: any[], isVisible: boolean) => {
        return groups.map((group) => ({
          id: group.group_id,
          sequence: group.sequence,
          name: group.group_name,
          description: group.group_description,
          visible: isVisible,
          groupDescription: group.group_description,
          features: group.features.map((feature: any) => ({
            id: feature.id,
            featureName: feature.feature_name,
            featureDescription: feature.feature_description,
            isChecked: false,
          })),
        }));
      };

      const transformedVisibleGroups = transformGroups(visibleGroups, true);
      const transformedHiddenGroups = transformGroups(hiddenGroups, false);

      setData({
        list: [...transformedVisibleGroups, ...transformedHiddenGroups],
      });
    } catch (error) {
      triggerToast("Error fetching permissions", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatureGroupsData();
  }, [triggerEffect]);

  const visibleGroups = useMemo(() => {
    let displayData = data?.list; // Default to all data

    if (searchValue?.trim() !== "") {
      displayData = data?.list?.filter((feature: any) =>
        feature?.name?.toLowerCase()?.includes(searchValue),
      );
    }

    displayData = displayData?.filter((item: any) => item?.visible === true);

    const rows: ValueStream[][] = [];
    if (displayData?.length) {
      for (let i = 0; i < displayData?.length; i += 4) {
        rows?.push(displayData?.slice(i, i + 4));
      }
    }

    return rows;
  }, [data, searchValue]);

  const hiddenGroups = useMemo(() => {
    let displayData = data?.list; // Default to all data

    if (searchValue?.trim() !== "") {
      displayData = data?.list?.filter((feature: any) =>
        feature?.name?.toLowerCase()?.includes(searchValue),
      );
    }
    displayData = displayData?.filter((item: any) => item?.visible === false);

    const rows: ValueStream[][] = [];
    if (displayData?.length) {
      for (let i = 0; i < displayData?.length; i += 4) {
        rows?.push(displayData?.slice(i, i + 4));
      }
    }

    return rows;
  }, [data, searchValue]);

  const handleSearchChange = (e: string) => {
    setSearchValue?.(e?.toLowerCase());
  };

  const handleSaveClick = () => {
    setIsEdit(false);
    setTriggerEffect((prev) => !prev);
  };

  return (
    <div
      className={scssClass.FeatureGroupContainer}
      style={{ display: "grid", gap: "16px" }}
    >
      <div>
        <div className={scssClass.container}>
          <div className={scssClass.header}>
            <div className={scssClass.title}>Feature Groups</div>
            {isEdit ? (
              <Box>
                <Button
                  variant="tertiary"
                  size="small"
                  onClick={() => setIsEdit(false)}
                  sx={{ height: "40px", marginRight: "8px" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleSaveClick}
                  sx={{ height: "40px" }}
                >
                  Save
                </Button>
              </Box>
            ) : (
              <Box>
                <Button
                  variant="tertiary"
                  size="small"
                  onClick={() => setIsEdit(true)}
                  sx={{ height: "40px", marginRight: "8px" }}
                >
                  Edit Groups
                </Button>

                <Button
                  variant="tertiary"
                  size="small"
                  onClick={() => setIsGroupSidePanelVisible(true)}
                  sx={{ height: "40px" }}
                >
                  Create Group
                </Button>
              </Box>
            )}
          </div>
        </div>

        <div className={scssClass.container}>
          <div className={scssClass.header}>
            <Search
              autoFocus={false}
              value={searchValue || ""}
              placeholder={"Search"}
              onChange={(e) => handleSearchChange(e)}
              onSubmit={() => {}}
              onClear={() => setSearchValue?.("")}
              className={scssClass.search}
              sx={{
                width: "333px",
                ".MuiButtonBase-root.MuiIconButton-root": {
                  padding: "0.5rem 0.75rem 0.5rem 0rem",
                  fontSize: "1.5rem",
                },
              }}
              onSearch={function (value: string): void {}}
            />
          </div>
        </div>
      </div>
      <div className={scssClass.sectionContainer}>
        {isLoading ? (
          <Box
            sx={{
              textAlign: "center",
              padding: "24px",
              fontSize: "16px",
              fontWeight: 500,
            }}
          >
            <LoadingSpinner fullScreen={true} />
          </Box>
        ) : (
          <>
            <FeatureGroupSection
              visibleGroups={visibleGroups}
              isVisible={true}
              fetchFeatureGroupsData={fetchFeatureGroupsData}
              isEdit={isEdit}
            />

            <FeatureGroupSection
              visibleGroups={hiddenGroups}
              isVisible={false}
              fetchFeatureGroupsData={fetchFeatureGroupsData}
              isEdit={isEdit}
            />
          </>
        )}
      </div>

      {isGroupSidePanelVisible && (
        <FeatureGroupSidebar
          open={isGroupSidePanelVisible}
          onClose={() => setIsGroupSidePanelVisible(false)}
          triggerEffect={triggerEffect}
          setTriggerEffect={setTriggerEffect}
        />
      )}
    </div>
  );
};

export default FeatureGroups;
